import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { AlertOctagon, Clock, Building2, MapPin, RefreshCw, LogIn, Loader2, UserX } from "lucide-react";
import { toast } from "sonner";
import ManualClockDialog from "./ManualClockDialog";

/**
 * Widget Tableau de bord — Retards de pointage (admin / manager uniquement).
 *
 * Liste en temps réel les shifts du jour dont le start_time + 5 min est dépassé
 * et pour lesquels aucun pointage d'entrée n'a été enregistré. Polling toutes
 * les 60 s. À chaque chargement, déclenche aussi la création de notifications
 * (idempotente) côté backend pour alerter admins + managers concernés.
 */
export default function LateArrivalsWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyShiftId, setBusyShiftId] = useState(null);
  const [clockDialog, setClockDialog] = useState({ open: false, target: null });

  const load = useCallback(async (notify = false) => {
    try {
      setRefreshing(true);
      const { data } = await api.get("/pointage/late-arrivals");
      setItems(data || []);
      if (notify) {
        // Idempotent : ne crée des notifs que pour les nouveaux retards.
        api.post("/pointage/late-arrivals/notify").catch(() => {});
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const openManualClockIn = (row) => {
    setClockDialog({
      open: true,
      target: {
        shift_id: row.shift_id,
        collaborateur_name: row.collaborateur_name,
        planned_start: row.start_time,
        planned_end: row.end_time,
      },
    });
  };

  const handleMarkAbsent = async (row) => {
    if (!window.confirm(
      `Déclarer ${row.collaborateur_name} absent pour ce shift ?\n\n` +
      `Le collaborateur apparaîtra dans la rubrique "Collaborateurs absents" et son intervention sera ajoutée aux "Interventions vacantes à combler". Action tracée dans l'historique de sécurité.`
    )) {
      return;
    }
    setBusyShiftId(row.shift_id);
    try {
      await api.post(`/pointage/late-arrivals/${row.shift_id}/mark-absent`);
      toast.success(`${row.collaborateur_name} déclaré absent`);
      await load(false);
      window.dispatchEvent(new CustomEvent("plania:absences-updated"));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Impossible de déclarer l'absence";
      toast.error(msg);
    } finally {
      setBusyShiftId(null);
    }
  };

  useEffect(() => {
    load(true);
    const id = setInterval(() => load(true), 60_000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div className="bg-white border border-zinc-200 rounded-sm" data-testid="late-arrivals-widget">
      <div className="px-4 sm:px-5 py-3 border-b border-zinc-200 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`h-7 w-7 inline-flex items-center justify-center rounded-sm flex-shrink-0 ${
            items.length > 0 ? "bg-red-50 text-red-700 border border-red-200" : "bg-zinc-50 text-zinc-400 border border-zinc-200"
          }`}>
            <AlertOctagon size={14} />
          </span>
          <div className="min-w-0">
            <p className="label-uppercase">Retard de pointage</p>
            <p className="text-[11px] text-zinc-500 font-mono truncate">
              Pointage d'entrée absent &gt; 5 min après le début du shift
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className={`text-[11px] font-mono px-2 py-0.5 rounded-sm ${
              items.length > 0 ? "bg-red-50 text-red-700 border border-red-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"
            }`}
            data-testid="late-arrivals-count"
          >
            {items.length} en retard
          </span>
          <button
            onClick={() => load(true)}
            className="text-zinc-500 hover:text-zinc-800 disabled:opacity-50"
            disabled={refreshing}
            title="Rafraîchir"
            data-testid="late-arrivals-refresh"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading ? (
        <p className="p-5 text-sm text-zinc-500 font-mono">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="p-5 text-center" data-testid="late-arrivals-empty">
          <p className="text-sm text-zinc-700">Aucun retard à signaler.</p>
          <p className="text-[11px] text-zinc-500 font-mono mt-1">
            Tous les collaborateurs en service ont pointé leur entrée ou sont dans la fenêtre tolérée.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {items.map((r) => (
            <li
              key={r.shift_id}
              className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid={`late-arrival-${r.shift_id}`}
            >
              <div className="flex-1 min-w-0">
                <Link
                  to={`/collaborateurs/${r.collaborateur_id}/dossier`}
                  className="text-sm font-medium text-zinc-900 hover:text-blue-700 hover:underline underline-offset-2"
                  data-testid={`late-arrival-collab-link-${r.shift_id}`}
                >
                  {r.collaborateur_name}
                </Link>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 mt-1 text-[11px] font-mono text-zinc-600">
                  <p className="inline-flex items-center gap-1.5">
                    <Clock size={11} className="text-zinc-400" />
                    Prévu à {r.start_time}
                    {r.end_time && <span className="text-zinc-400">→ {r.end_time}</span>}
                  </p>
                  <p className="inline-flex items-center gap-1.5">
                    <Building2 size={11} className="text-zinc-400" /> {r.brand_name}
                  </p>
                  {r.site_name && (
                    <p className="inline-flex items-center gap-1.5">
                      <MapPin size={11} className="text-zinc-400" /> {r.site_name}
                    </p>
                  )}
                </div>
              </div>
              <span
                className="text-[11px] font-mono font-semibold px-2 py-1 rounded-sm bg-red-50 text-red-700 border border-red-200 flex-shrink-0"
                data-testid={`late-arrival-delay-${r.shift_id}`}
              >
                +{r.delay_minutes} min
              </span>
              <button
                onClick={() => openManualClockIn(r)}
                disabled={busyShiftId === r.shift_id}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-sm bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0 transition-colors"
                title={`Pointer l'entrée à la place de ${r.collaborateur_name}`}
                data-testid={`late-arrival-manual-clock-in-${r.shift_id}`}
              >
                {busyShiftId === r.shift_id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <LogIn size={12} />
                )}
                Pointer entrée collaborateur
              </button>
              <button
                onClick={() => handleMarkAbsent(r)}
                disabled={busyShiftId === r.shift_id}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-sm bg-red-700 text-white hover:bg-red-800 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0 transition-colors"
                title={`Déclarer ${r.collaborateur_name} absent`}
                data-testid={`late-arrival-mark-absent-${r.shift_id}`}
              >
                <UserX size={12} />
                Collaborateur absent
              </button>
            </li>
          ))}
        </ul>
      )}
      <ManualClockDialog
        open={clockDialog.open}
        onOpenChange={(v) => setClockDialog((s) => ({ ...s, open: v }))}
        mode="in"
        target={clockDialog.target}
        onSuccess={() => load(false)}
      />
    </div>
  );
}
